import { Language } from '@/types';
import { ChatMessage, ChatUser, Conversation } from '@/services/chatApi';

const LOCALES: Record<Language, string> = {
  ru: 'ru-RU',
  en: 'en-US',
  fr: 'fr-FR',
};

const localeOf = (language: Language): string => LOCALES[language] || 'en-US';

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const displayName = (user?: ChatUser | null): string => {
  if (!user) return '';
  return user.name?.trim() || user.username || '';
};

export const conversationTitle = (
  conversation: Conversation,
  fallback: string
): string => {
  if (conversation.kind === 'group') return conversation.title || fallback;
  return displayName(conversation.peer) || fallback;
};

export const conversationAvatar = (
  conversation: Conversation
): string | undefined => {
  if (conversation.kind === 'group') return undefined;
  return conversation.peer?.avatar || undefined;
};

/** Инициалы для аватара группы: «Семейный чат» → «СЧ». */
export const initialsOf = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() || '')
    .join('');

/** Время сообщения в пузыре: 14:05. */
export const formatMessageTime = (
  isoDate: string,
  language: Language
): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString(localeOf(language), {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Метка в списке чатов: время — для сегодняшних, иначе дата. */
export const formatConversationTimestamp = (
  isoDate: string | null | undefined,
  language: Language
): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  const isToday = startOfDay(date) === startOfDay(new Date());
  if (isToday) return formatMessageTime(isoDate, language);

  return date.toLocaleDateString(localeOf(language), {
    day: '2-digit',
    month: '2-digit',
  });
};

/** Разделитель дня в ленте сообщений. */
export const formatDaySeparator = (
  isoDate: string,
  language: Language,
  labels: { today: string; yesterday: string }
): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  const dayDiff = Math.round(
    (startOfDay(new Date()) - startOfDay(date)) / 86400000
  );
  if (dayDiff === 0) return labels.today;
  if (dayDiff === 1) return labels.yesterday;

  return date.toLocaleDateString(localeOf(language), {
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() === new Date().getFullYear()
      ? {}
      : { year: 'numeric' }),
  });
};

export const isSameDay = (firstIso: string, secondIso: string): boolean =>
  startOfDay(new Date(firstIso)) === startOfDay(new Date(secondIso));

/** Подстановка значений в строку перевода: «{actor} добавил(а) {users}». */
export const formatTemplate = (
  template: string,
  values: Record<string, string>
): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? values[key] : match
  );

/** Человекочитаемый текст системного события чата. */
export const systemMessageText = (
  message: ChatMessage,
  t: (key: string) => string
): string => {
  const payload = (message.systemPayload || {}) as {
    users?: { id: string; username: string }[];
    title?: string;
    from?: string;
    to?: string;
  };
  const actor = displayName(message.sender) || t('chat.system.someone');
  const users = (payload.users || []).map(user => user.username).join(', ');

  const templateKey = `chat.system.${camelCase(message.systemEvent || '')}`;
  const template = t(templateKey);

  if (template === templateKey) return '';
  return formatTemplate(template, {
    actor,
    users,
    title: payload.title || payload.to || '',
    from: payload.from || '',
    to: payload.to || '',
  });
};

/** Короткое описание сообщения для списка диалогов. */
export const messagePreviewText = (
  message: ChatMessage | null,
  t: (key: string) => string,
  currentUserId?: string
): string => {
  if (!message) return t('chat.list.noMessages');
  if (message.kind === 'system') return systemMessageText(message, t);

  const body =
    message.text ||
    (message.capsules.length > 0 ? t('chat.list.capsuleAttachment') : '');

  if (message.sender && message.sender.id === currentUserId) {
    return formatTemplate(t('chat.list.ownPrefix'), { text: body });
  }
  return body;
};

const camelCase = (value: string): string =>
  value.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());

/**
 * Команда «/» в начале слова открывает подсказку капсул.
 * Поддерживается и «\» — чтобы не зависеть от раскладки клавиатуры.
 */
const SLASH_COMMAND_PATTERN = /(?:^|\s)([/\\])([^\s/\\]*)$/;

export interface SlashCommand {
  /** Текст после слэша — поисковый запрос по капсулам. */
  query: string;
  /** Позиция самого слэша: с неё команда вырезается из текста. */
  startIndex: number;
}

export const detectSlashCommand = (
  value: string,
  caretPosition: number
): SlashCommand | null => {
  const match = value.slice(0, caretPosition).match(SLASH_COMMAND_PATTERN);
  if (!match) return null;

  return {
    query: match[2],
    startIndex: caretPosition - match[2].length - 1,
  };
};
